import { HierarcicalObjectReference } from "@novorender/data-js-api";

import { Node, Tree } from "components/objectTree/types";
import { getParentPaths } from "components/objectTree/utils";
import { ModelTreeNodeData } from "features/modelTree/types";
import { NodeType } from "types/misc";
import { isLeafNode } from "utils/objectPath";

/**
 * Creates a tree node. The only difference between a leaf-like match and an ancestor
 * node is the expanded state.
 *
 * @param path - The object path
 * @param obj - Optional hierarchical object reference (undefined for parent-only nodes)
 * @param isAncestor - If true, node is an ancestor on the path down to some other match —
 *                   either a synthetic placeholder (`obj` undefined, for path hierarchy that
 *                   didn't match the search itself) or a match that is ALSO an ancestor of
 *                   another, deeper match (`obj` set). Either way it's auto-expanded, with a
 *                   `children` array pre-populated below with only the matches found beneath
 *                   it. `hasMoreChildren` is set so ModelTree's existing background loader
 *                   (the same one that drains paginated folders in the unfiltered tree)
 *                   transparently fetches and merges in the rest of its REAL children too —
 *                   every node stays fully expandable/browsable, the search only decides
 *                   what's pre-opened and highlighted, not what's reachable.
 *                   If false, node is a real search result with no further match beneath it.
 *                   Its `children` are intentionally left unloaded (`undefined`), even for
 *                   non-leaf (folder) matches, so expanding it triggers a real DB fetch of its
 *                   full hierarchy — exactly like the unfiltered tree — instead of being
 *                   artificially capped. This lets the user browse and toggle visibility on
 *                   every real child, not only the ones that happened to match the search term.
 */
function createNode(
    path: string,
    obj: HierarcicalObjectReference | undefined,
    isAncestor: boolean,
): Node<ModelTreeNodeData> {
    const pathParts = path.split("/");
    const name = pathParts[pathParts.length - 1];

    const isLeaf = obj ? isLeafNode(obj) : false;

    return {
        path: obj?.path ?? path,
        name,
        expanded: isAncestor,
        loading: false,
        isLeaf,
        children: isAncestor ? [] : undefined,
        hasMoreChildren: isAncestor,
        data: {
            object:
                obj ??
                ({
                    id: -1,
                    path,
                    type: NodeType.Internal,
                } as HierarcicalObjectReference),
        },
    };
}

/**
 * @param isAncestor - Pass `true` when this match is ALSO an ancestor of some other, deeper
 *   match — see {@link createNode}'s `isAncestor` param. Defaults to `false` (plain leaf-like
 *   match, collapsed with unloaded children).
 */
export function createNodeFromObject(obj: HierarcicalObjectReference, isAncestor = false): Node<ModelTreeNodeData> {
    return createNode(obj.path, obj, isAncestor);
}

export function createParentNode(path: string): Node<ModelTreeNodeData> {
    return createNode(path, undefined, true);
}

/**
 * Builds a filtered tree structure from database search results.
 *
 * This function is necessary because the ModelTree uses lazy loading - unexpanded folders
 * don't have their children in memory. To search ALL objects (not just visible ones),
 * we must query the database directly and build a new tree from the results.
 *
 * Note: We cannot use filterOutObjectsNotOnPath() from modelTree/utils.ts because it only
 * filters nodes that already exist in the tree. Since the tree is lazy-loaded, most nodes
 * don't exist in memory until explicitly expanded by the user.
 *
 * @param matchingObjects - Array of objects from database search results
 * @param topLevelObjects - Every real top-level folder (typically one per file), regardless
 *   of whether it has a match beneath it — usually the already-loaded root children from the
 *   base/preloaded tree. Passing these ensures the top level always shows the full set of
 *   files, not just the ones a match happened to fall under. Folders with no match are added
 *   collapsed with unloaded (lazy) children, same as the unfiltered tree, so expanding one
 *   triggers a normal on-demand fetch instead of relying on the (disabled during search)
 *   background drain used for synthetic ancestors deeper in the tree.
 * @returns A new tree containing the matching objects and their parent hierarchy, plus every
 *   top-level folder
 */
export function buildFilteredTree(
    matchingObjects: HierarcicalObjectReference[],
    topLevelObjects: HierarcicalObjectReference[] = [],
): Tree<ModelTreeNodeData> {
    const filteredTree: Tree<ModelTreeNodeData> = {
        "": {
            path: "",
            name: "Root",
            expanded: true,
            loading: false,
            isLeaf: false,
            children: [],
            // `topLevelObjects` below fills in every real top-level folder already known at
            // search time, but a huge model's root might not have finished paginating yet.
            // Kept `true` so that, once the search is cleared, ModelTree's existing background
            // loader still fills in any further top-level folders it hasn't seen (the loader
            // itself is skipped while a search is active — see modelTree.tsx's drainChildren).
            hasMoreChildren: true,
            data: {
                object: { id: -1, path: "", type: NodeType.Internal } as HierarcicalObjectReference,
            },
        },
    };

    // Collect every ancestor path (at every level) of every match, BEFORE creating any nodes,
    // so we know up front which matches are themselves an ancestor of some other, deeper match.
    const pathsToCreate = new Set<string>();
    for (const obj of matchingObjects) {
        for (const parentPath of getParentPaths(obj.path, false)) {
            pathsToCreate.add(parentPath);
        }
    }

    // Create nodes for all matching objects. All matches must be expanded down to every other
    // match they contain — so a match whose own path is also an ancestor of another match (i.e.
    // in `pathsToCreate`) is expanded with its known descendant match(es) pre-linked below,
    // exactly like a synthetic ancestor, instead of being collapsed with unloaded children.
    // Otherwise those nested matches would be built into the tree map but never linked in as
    // reachable from root.
    for (const obj of matchingObjects) {
        filteredTree[obj.path] = createNodeFromObject(obj, pathsToCreate.has(obj.path));
    }

    // Create synthetic ancestor placeholders for parent paths that aren't themselves a match.
    // Paths that ARE a match (`filteredTree[path]` already set above) keep their own node.
    for (const path of pathsToCreate) {
        if (!filteredTree[path]) {
            filteredTree[path] = createParentNode(path);
        }
    }

    const allPaths = [...pathsToCreate, ...matchingObjects.map((obj) => obj.path)];

    // Link children to parents
    for (const path of allPaths) {
        const pathParts = path.split("/");
        const parentPath = pathParts.slice(0, -1).join("/");

        const parent = filteredTree[parentPath];
        if (parent && parent.children && !parent.children.includes(path)) {
            parent.children.push(path);
        }
    }

    // Merge in every other known top-level folder so the top level always shows the full
    // set of files, even those with no match beneath them (see param docs above).
    for (const obj of topLevelObjects) {
        if (!filteredTree[obj.path]) {
            filteredTree[obj.path] = createNodeFromObject(obj);
        }
        if (!filteredTree[""].children!.includes(obj.path)) {
            filteredTree[""].children!.push(obj.path);
        }
    }

    return filteredTree;
}
